// App.tsx
import React, { useState, useEffect, useRef } from 'react';
import { LandingPage } from './components/LandingPage';
import { AuthPage, AuthMode } from './components/AuthPage';
import { AdminConsole } from './components/AdminConsole';
import { ATypicalModule } from './components/ATypicalModule';
import { ShapeShifterModule } from './components/ShapeShifterModule';
import { PracticeMakesPermanentModule } from './components/PracticeMakesPermanentModule';
import { TheAssessorModule } from './components/TheAssessorModule';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { Role, Tenant } from './types';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import { usePersistentLogos } from './hooks/usePersistentLogos';
import { signOut } from 'firebase/auth';
import { auth } from './firebase/config';

type PublicScreen = 'landing' | 'auth';
type ShellScreen = 'modules' | 'admin';

const AppContent: React.FC = () => {
const { user, profile, tenant, partner, loading, authError, updateTenant, gateRequired, gateEmail, confirmGate, rejectGate } = useAuth();
  const { brandLogo, partnerLogo } = usePersistentLogos();

  const [publicScreen, setPublicScreen] = useState<PublicScreen>('landing');
  const [authMode, setAuthMode] = useState<AuthMode>('existing');
  const [shellScreen, setShellScreen] = useState<ShellScreen>('modules');

  const prevSignedInRef = useRef<boolean>(false);

  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log('[App] publicScreen =', publicScreen);
  }, [publicScreen]);

  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log('[App] user =', user ? user.uid : null);
  }, [user]);

  
  const isSignedIn = !!user;
  if (isSignedIn && gateRequired) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md w-full border rounded-lg p-5 bg-white">
          <div className="text-sm text-gray-700">Continue as</div>
          <div className="mt-1 font-semibold text-gray-900">{gateEmail || 'Signed-in user'}</div>

          <div className="mt-4 flex gap-3">
            <button className="px-4 py-2 rounded bg-gray-900 text-white" onClick={confirmGate}>
              Continue
            </button>
            <button className="px-4 py-2 rounded border" onClick={rejectGate}>
              Sign out
            </button>
          </div>
        </div>
      </div>
    );
  }
  const hasProfile = !!profile;

  const isAdminRole =
    profile?.role === Role.PLATFORM_ADMIN ||
    profile?.role === Role.PARTNER_ADMIN ||
    profile?.role === Role.TENANT_ADMIN;

 // Signed out: render exactly one screen (prevents focus loss due to hide/show)
if (!isSignedIn) {
  if (publicScreen === 'landing') {
    return (
      <LandingPage
        brandLogoUrl={brandLogo}
        partnerLogoUrl={partnerLogo}
        onGetStarted={() => {
          setAuthMode('existing');
          setPublicScreen('auth');
        }}
      />
    );
  }

  return <AuthPage mode={authMode} onModeChange={setAuthMode} />;
}
  // Signed in: bootstrap states
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-600">
        Loading...
      </div>
    );
  }

  if (authError) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-xl w-full border rounded-lg p-4 text-sm text-red-700 bg-red-50">
          {authError}
        </div>
      </div>
    );
  }

    if (!hasProfile) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-xl w-full border rounded-lg p-4 text-sm text-gray-700 bg-white">
          Profile not available. Use Sign out, then sign in again.
        </div>
      </div>
    );
  }

  const tenantScoped =
    profile.role === Role.TENANT_ADMIN ||
    profile.role === Role.TEACHER_USER;

  if (tenantScoped && !tenant) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-600">
        Tenant missing or inaccessible.
      </div>
    );
  }

  // Signed in and bootstrapped: app shell
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header
        userProfile={profile}
        tenant={tenant as Tenant}
  partner={partner}
        onSignOut={() => {
          (async () => {
            try {
setPublicScreen('landing');
              setAuthMode('existing');
              setShellScreen('modules');

              try { window.localStorage.removeItem('osfn_pending_invite_id'); } catch (_) {}
              try { await signOut(auth); } catch (_) {}
            } finally {
window.location.href = '/1sf0/';            }
          })();
        }}
        partnerLogoUrl={partnerLogo}
      />

      <main className="flex-1">
        {shellScreen === 'admin' && isAdminRole ? (
          <AdminConsole
            userProfile={profile}
            tenant={tenant as Tenant}
            onExitAdmin={() => setShellScreen('modules')}
            updateTenant={updateTenant}
          />
        ) : (
          <div className="max-w-6xl mx-auto px-4 py-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ATypicalModule tenant={tenant} userProfile={profile} brandLogo={brandLogo} partnerLogo={partnerLogo} />
            <ShapeShifterModule tenant={tenant} userProfile={profile} brandLogo={brandLogo} partnerLogo={partnerLogo} />
            <PracticeMakesPermanentModule tenant={tenant} userProfile={profile} brandLogo={brandLogo} partnerLogo={partnerLogo} />
            <TheAssessorModule tenant={tenant} userProfile={profile} brandLogo={brandLogo} partnerLogo={partnerLogo} />
          </div>
        )}
      </main>

      <Footer
        onOpenAdmin={() => {
          if (isAdminRole) setShellScreen('admin');
        }}
        isAdminRole={!!isAdminRole}
      />
    </div>
  );
};

export default function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}